import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { Field, FormikProps } from 'formik'
import {
  EditionType,
  EditionSubType,
} from '@graphics/gfx-common/dist/constants'
import { IPackInput, EditionLanguageInput } from '../../types/pack'
import { ILanguages } from '../../types/languages'
import { IServerConfig } from '../../types/config'
import { validateSlug } from '../../actions/pack'
import { getSluglineSuggestions } from '../../services/typeahead'
import { createPreviewFieldSetter } from '../../services/file'
import {
  buildTitleHelpText,
  buildDescriptionHelpText,
} from '../../services/editionsHelpers'
import { concatenate, getPreviewImages } from '../../services/pack'
import {
  transformPackInputToState,
  checkIsLanguageInherit,
  getEditionLanguage,
  getEditionLanguageInputValue,
} from '../../services/packForm'
import {
  CustomTextField,
  CustomSelectField,
  CustomCheckboxField,
  CustomTypeheadSlugline,
} from '../../components'
import { EditionPreviewFormFragment } from './EditionPreviewFormFragment'
import { DatastreamGraphicURLFormFragment } from './DatastreamGraphicURLFormFragment'
import { StyledEditionFormFragment } from './styled'
import {
  validateEditionSlug,
  validateEditionCaption,
  validateEditionTitle,
} from './validation'

export interface IEditionFormFragment {
  index: number
  languages: ILanguages
  isFormDisabled?: boolean
  serverConfig: IServerConfig
  formikProps: FormikProps<IPackInput>
  dispatch: Dispatch
}

export const EditionFormFragmentComponent = ({
  index,
  serverConfig,
  languages,
  isFormDisabled = false,
  formikProps: { values: pack, errors, validateForm, setFieldValue },
  dispatch,
}: IEditionFormFragment) => {
  useEffect(() => {
    validateForm()
  }, [index, validateForm])

  const edition = pack.editions && pack.editions[index]
  if (!edition) {
    return null
  }

  const isLanguageInherit = checkIsLanguageInherit(
    pack.language,
    edition.language
  )

  const isDataStreamGraphic =
    edition.editionType === EditionType.DataStreamGraphic

  const sluglineBlurHandler = ({ target: { value: slugValue } }) => {
    if (!edition.addToPackSlug && !!slugValue && !errors.slugline) {
      dispatch(
        validateSlug(
          slugValue,
          getEditionLanguage(pack.language, edition.language),
          (value) => {
            setFieldValue(`editions.${index}.slugline`, value)
          }
        )
      )
    }
  }
  return (
    <StyledEditionFormFragment>
      <Field
        name={`editions.${index}.language`}
        label="Language"
        component={CustomSelectField}
        value={getEditionLanguageInputValue(pack.language, edition.language)}
        onChange={(e) => {
          if (checkIsLanguageInherit(pack.language, e.target.value)) {
            setFieldValue(`editions[${index}].addToPackSlug`, true)
            e.target.value = EditionLanguageInput.usePackValue
          }
        }}
        disabled={!!isFormDisabled}
      >
        {[
          {
            value: EditionLanguageInput.usePackValue,
            label: 'Use pack language',
          },
          ...languages.list,
        ]}
      </Field>
      <Field
        multiline
        trimValueOnBlur
        name={`editions.${index}.title`}
        label="File title"
        placeholder="Add to pack title"
        component={CustomTextField}
        helperText={buildTitleHelpText(
          edition,
          isLanguageInherit ? pack.title : ''
        )}
        validate={validateEditionTitle(isLanguageInherit, pack.title)}
        validateImmediately
        disabled={!!isFormDisabled}
      />
      <Field
        multiline
        trimValueOnBlur
        name={`editions.${index}.description`}
        label="File caption"
        placeholder="Add to pack caption"
        component={CustomTextField}
        helperText={buildDescriptionHelpText(
          edition,
          isLanguageInherit ? pack.description : ''
        )}
        validate={validateEditionCaption(isLanguageInherit, pack.description)}
        validateImmediately
        disabled={!!isFormDisabled}
      />
      {isDataStreamGraphic && (
        <DatastreamGraphicURLFormFragment
          index={index}
          disabled={isFormDisabled}
          allowedDomains={serverConfig.allowedDomains}
        />
      )}
      {!isLanguageInherit && (
        <Field
          name={`editions.${index}.addToPackSlug`}
          id={`editions.${index}.addToPackSlug`}
          component={CustomCheckboxField}
          label="Use pack slug"
          value={isLanguageInherit}
          disabled={!!isFormDisabled}
        />
      )}
      <Field
        name={`editions.${index}.slugline`}
        label="Slug"
        placeholder={
          isLanguageInherit || edition.addToPackSlug
            ? 'Add to pack slug'
            : 'Add slug'
        }
        changeValue={(value) => value.toUpperCase()}
        helperText={concatenate(
          transformPackInputToState(pack),
          edition,
          'slugline'
        )}
        validate={validateEditionSlug(
          isLanguageInherit || edition.addToPackSlug,
          pack.slugline
        )}
        validateImmediately
        component={CustomTypeheadSlugline}
        loadOptions={getSluglineSuggestions}
        onBlur={sluglineBlurHandler}
        disabled={!!isFormDisabled}
      />
      {edition.editionSubType === EditionSubType.Interactive && (
        <>
          <Field
            multiline
            name={`editions.${index}.embed.declaration`}
            label="Embed declaration"
            component={CustomTextField}
            validateImmediately
            disabled={!!isFormDisabled}
          />
          <Field
            multiline
            name={`editions.${index}.embed.dependencies`}
            label="Embed dependencies"
            component={CustomTextField}
            disabled={!!isFormDisabled}
          />
        </>
      )}
      <EditionPreviewFormFragment
        index={index}
        edition={edition}
        packThumbnailURI={getPreviewImages(pack).thumbnailURI}
        setPreviewFieldWrapper={createPreviewFieldSetter(
          setFieldValue,
          `editions.${index}.`
        )}
        isFormDisabled={!!isFormDisabled}
        errors={errors}
      />
    </StyledEditionFormFragment>
  )
}

export const EditionFormFragment = connect()(EditionFormFragmentComponent)
